import { intersection } from "lodash";
import { EXPIRATION_OCTOBER_2025, EXPIRATION_OCTOBER_2026 } from ".";

// const FIRST_PRORATED_DISCOUNT_23_24 = 0.4;
// const SECOND_PRORATED_DISCOUNT = 0.65; // update on 3/15/24 ?

export const CURRENT_SCHOOL_YEAR = "24-25 Full Year";
export const CURRENT_PRORATED_LICENSE = "24-25 Prorated";
export const NEXT_SCHOOL_YEAR = "25-26 Full Year";

export const EXPIRATIONS: Record<
  | typeof CURRENT_SCHOOL_YEAR
  | typeof CURRENT_PRORATED_LICENSE
  | typeof NEXT_SCHOOL_YEAR,
  number
> = {
  [CURRENT_SCHOOL_YEAR]: EXPIRATION_OCTOBER_2025,
  [CURRENT_PRORATED_LICENSE]: EXPIRATION_OCTOBER_2025,
  [NEXT_SCHOOL_YEAR]: EXPIRATION_OCTOBER_2026,
};

/**
 * Describes the type of renewal actions which exist.
 */
export type IRenewalType =
  | "school"
  | "individual"
  | "9-12"
  | "6-12"
  | "6-8"
  | "7-12"
  | "7-8"
  | "8-12"
  | "school_custom";

export type InvoiceLicenseType =
  | "6-12"
  | "9-12"
  | "6-8"
  | "7-12"
  | "7-8"
  | "school"
  | "individual";

export type InvoiceSiteLicenseType =
  | "6-12"
  | "9-12"
  | "6-8"
  | "7-12"
  | "7-8"
  | "school";

export type PriceSchemeYear = "23-24 Prorated" | "24-25 Full Year";

export type TLicenseType = //reflects quoted products, not necessarily every license option possible

    | "District License (Grades 6-12)"
    | "District License (Grades 7-12)"
    | "District License (Grades 9-12)"
    | "District License (Grades 6-8)"
    | "District License (Grades 7-8)"
    | "School License"
    | "Individual License";

export type TGradeRange = "6-12" | "7-12" | "9-12" | "6-8" | "7-8"; //for quoted district products

export const districtLicenseQuoteTypes = ["6-12", "7-12", "9-12", "6-8", "7-8"]; //reflects quoted products, not necessarily every license option possible

export interface IDistrictData {
  districtID: string;
  dmLicense: {
    emailDomains: string[];
    license_type: "6-8" | "6-12" | "9-12" | "8-12" | "7-12" | "7-8"; // but we make exceptions
    num_students: number;
    num_teachers?: number;
    invoice: number;
    expiration: number;
    revenue: number;
    integrations: string;
    note?: string;
    trial_6_8?: boolean;
    has_integral?: boolean;
    hide_admin_portal?: boolean;
    force_show_admin_portal?: boolean;
    is_trial?: boolean;
  };
  districtName: string;
  match: {
    name: string;
    email: string;
  };
  super_admin: {
    email: string;
    name: string;
  };
  nces_flag?: boolean;
  nces_notes?: string;
  invoiceNumber?: string;
}

export interface ISchoolPopData {
  students: any;
  lowGrade: any;
  highGrade: any;
  name: string;
  nces_id: string;
  numberOfTeachers: number;
  super_admin: {
    email: string;
    name: string;
  };
  invoiceNumber?: string;
  coveredByDistLicense?: boolean;
}

export interface ISchoolData {
  /** Unique schoolid of the school, typically an nces id but in some cases may be driven by a classlink or clever integration. */
  schoolid: string;
  /** Name of the school, when combined with address.zip is considered unique. */
  schoolName: string;
  /** The customer id in QuickBooks (where applicable) */
  qboCustomerId?: number;

  phone?: string;
  url?: string;
  urlCompare?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    stateFull?: string;
    zip: string;
    zip4?: string;
    cityURL?: string;
    zipURL?: string;
    html?: string;
  };
  lowGrade: string;
  highGrade: string;
  schoolLevel?: string;
  isCharterSchool?: string;
  isMagnetSchool?: string;
  isVirtualSchool?: string;
  isTitleISchool?: string;
  isTitleISChoolwideSchool?: string;
  district: {
    districtID: string;
    districtName: string;
    url?: string;
    rankURL?: string;
  };
  county?: {
    countyName: string;
    countyURL: string;
  };
  rankMovment?: number;
  isPrivate?: boolean;
  privateDays?: any;
  privateHours?: any;
  privateHasLibrary?: any;
  privateCoed?: any;
  privateOrientation?: any;
  dmLicense: {
    num_students?: number;
    num_teachers?: number;
    license_type?: string;
    invoice?: number;
    revenue?: number;
    expiration?: number;
    integrations?: string;
    domains: string[];
    note?: string;
    trial_6_8?: boolean;
    has_integral?: boolean;
    hide_admin_portal?: boolean;
    force_show_admin_portal?: boolean;
    is_trial?: boolean;
  };
  dmLicenseHistory?: ISchoolData["dmLicense"][];
  quoteStatus22?: {
    hasQuote: boolean;
    totalTeachers: number;
    totalLogs: number;
    logsPerTeacher: number;
  };
  timezone: string;
  rostering_ids?: string[];
  nces_flag?: boolean;
  nces_notes?: string;
}

export interface IPriceAndDiscountInfo {
  price: {
    plus: number;
    integral: number;
  };
  numberOfStudents?: number;
  discount: number;
  priceWithDiscount: {
    plus: number;
    integral: number;
  };
  minPrice?: {
    plus: number;
    integral: number;
  };
  schools?: ISchoolPopData[];
  licenseType?: TLicenseType;
  sendCustomerEmail?: boolean;
}

type IAdmin = {
  name: string;
  email: string;
  districtID: string;
  schoolIDs: string[];
  account_type: "school" | "district" | "super_school" | "super_district";
};

export interface ISchoolSearchResult {
  type: "school";
  schooldigger: ISchoolData;
  inferred_timezone: string;
}

export interface IDistrictSearchResult {
  type: "district";
  schooldigger: IDistrictData;
  inferred_timezone: string;
}

export type ISchoolDiggerSearchResult =
  | ISchoolSearchResult
  | IDistrictSearchResult;

export type LicenseTree = {
  District: {
    districtID: string;
    districtName?: string;
    numberAdmins?: number;
    dmLicense?: IDistrictData["dmLicense"];
    match?: Partial<IAdmin>;
    super_admin?: IAdmin;
    nces_flag?: any;
    nces_notes?: string;
    quoteNumber?: any;
    adminsSearched?: Array<IAdmin>;
  };
  Schools: Array<{
    schoolid: string;
    schoolName?: string;
    numberTeachers?: number;
    numberAdmins?: number;
    dmLicense?: ISchoolData["dmLicense"];
    match?: Partial<IAdmin>;
    super_admin?: {
      name?: string;
      email?: string;
    };
    admin?: {
      name?: string;
      email?: string;
    };
    nces_flag?: any;
    nces_notes?: string;
    quoteNumber?: any;
    adminsSearched?: Array<IAdmin>;
  }>;
};

export interface LicenseInfo {
  type: string;
  id: string;
  licenseTier: "Plus" | "Integral";
  expiration: number;
  integrations: string;
  schoolAndDistLicense?: {
    licenseTier: "Plus" | "Integral";
    integrations: string;
    expiration: number;
  };
  admin?: { name: string; email: string };
}

export const DISTRICT_LICENSE_TO_GRADES: {
  //reflects quoted products, not necessarily every license option possible
  [key: string]: { low: number; high: number };
} = {
  "District License (Grades 6-12)": {
    low: 6,
    high: 12,
  },
  "District License (Grades 7-12)": {
    low: 7,
    high: 12,
  },
  "District License (Grades 9-12)": {
    low: 9,
    high: 12,
  },
  "District License (Grades 6-8)": {
    low: 6,
    high: 8,
  },
  "District License (Grades 7-8)": {
    low: 7,
    high: 8,
  },
};

//utility for getting the general license type. Not be confused w/ coerceLicenseType, which is effectively a migration utility to convert older license types to the new ones.
export function discriminateInvoiceType(
  type: string
): "school" | "district" | "individual" {
  // TODO 04/14/25 - remove after debugging
  console.log("discriminateInvoiceType", { type });
  switch (type) {
    case "school":
    case "School License":
    case "school_custom":
      return "school";
    case "6-12":
    case "7-12":
    case "7-8":
    case "6-8":
    case "8-12":
    case "9-12":
      return "district";
    case "individual":
      return "individual";

    default:
      throw new Error(`Invalid type: ${type}`);
  }
}

//determines whether a site license/quote should be treated as SNN
export function isSNNCase({
  type,
  id,
}: {
  type: string | undefined;
  id: string | undefined | null;
}): boolean {
  if (
    type === "school_custom" ||
    (typeof id === "string" && id?.includes("custom"))
  ) {
    return true;
  } else if (!id || id === "-1") {
    return true;
  } else {
    return false;
  }
}

export function getSchoolLowHigh(school: any): { low: number; high: number } {
  /**
   * returns the lowest and highest grades a school encompasses
   */
  let low = school.lowGrade;
  if (low === "K") low = 0;
  else if (low === "PK") low = -1;
  else low = parseInt(low);
  if (isNaN(low)) low = 6;

  let high = school.highGrade;
  if (high === "K") high = 0;
  else if (high === "PK") high = -1;
  else high = parseInt(school.highGrade);
  if (isNaN(high)) high = 12;
  return { low, high };
}

export function getStudentGradesCounts(schools: any[]): {
  elemTotal: number;
  middleTotal: number;
  highTotal: number;
  studentsTotal: number;
} {
  let elemTotal = 0;
  let middleTotal = 0;
  let highTotal = 0;
  let studentsTotal = 0;
  for (const org of schools) {
    org.students = parseInt(org.students);
    if (org.students) studentsTotal += org.students;
    else continue;

    let low = org.lowGrade;
    if (low === "K") low = 0;
    else if (low === "PK") low = -1;
    else low = parseInt(low);
    if (isNaN(low)) low = 6;
    let high = parseInt(org.highGrade);
    if (isNaN(high)) high = 12;

    // relative proportion in PK-5, 6-8, 9-12
    let weight1 = 0;
    let weight2 = 0;
    let weight3 = 0;
    let totalWeight = 0;
    for (let i = low; i <= high; i++) {
      totalWeight++;
      if (i <= 5) weight1++;
      else if (i <= 8) weight2++;
      else weight3++;
    }
    elemTotal += Math.round((weight1 / totalWeight) * org.students);
    middleTotal += Math.round((weight2 / totalWeight) * org.students);
    highTotal += Math.round((weight3 / totalWeight) * org.students);
  }
  return { elemTotal, middleTotal, highTotal, studentsTotal };
}

export function filterSchoolsByGrade(
  schools: any[],
  licenseType: TLicenseType
) {
  const filteredSchools: ISchoolPopData[] = [];
  schools?.forEach((school: ISchoolPopData) => {
    const { low, high } = getSchoolLowHigh(school);
    // check if school overlaps with license, if so add it to filteredSchools
    if (licenseType === "School License") filteredSchools.push(school);
    else {
      const gradeRange = DISTRICT_LICENSE_TO_GRADES[licenseType];
      const schoolInRange =
        (low >= gradeRange.low && low <= gradeRange.high) ||
        (high <= gradeRange.high && high >= gradeRange.low) ||
        (low < gradeRange.low && high > gradeRange.high);

      if (schoolInRange) filteredSchools.push(school);
    }
  });
  return filteredSchools;
}

export type TLicenseRange =
  | TGradeRange
  | "school"
  | "school_custom"
  | "individual";

export const getLicenseDescription = (
  type: TLicenseRange | undefined,
  tier: "plus" | "integral" | undefined
): `${TLicenseType} - ${"Plus" | "Integral"}` | "" => {
  if (!type || !tier) return "";
  const capTier: "Plus" | "Integral" = tier === "plus" ? "Plus" : "Integral";
  return type === "school"
    ? `School License - ${capTier}`
    : `${rangeToFullType[type] as TLicenseType} - ${capTier}`;
};

// export interface ILicenseOptions {
//   current: TLicenseOption;
//   recommended_upgrade: TLicenseOption | null;
//   entire_district: TLicenseOption[] | null;
// }

export interface IRenewalData {
  schoolYear: string;
  licenseName: string;
  nces: string;
  type: TLicenseRange;
  licenseType: TLicenseType;
  hasIntegral: boolean;
  licenseOptions: Array<[TLicenseOption, TLicenseOption] | TLicenseOption>;
  integrations: TIntegration;
  integral_trial?: boolean;
}

export interface TLicenseOption {
  type: TLicenseType;
  tier: "Plus" | "Integral";
  price: number;
  pathId: any;
}

export type TIntegration =
  | "none"
  | "clever"
  | "classlink/oneroster"
  | "schoology"
  | "canvas"
  | "classlink";

export interface IRenewalRequestBody {
  type: TLicenseType;
  licenseTier: "Plus" | "Integral";
  rosteringSystem?:
    | "ClassLink/OneRoster"
    | "Clever"
    | "Schoology"
    | "Canvas"
    | "None"
    | "";
  learningManagementSystem?: "Schoology" | "Canvas" | "None" | "";
  renewalToken: string;
  currentLicenseType: TLicenseType;
  currentHasIntegral: boolean;
  pathId: any;
}

export const rangeToFullType: {
  "6-12": "District License (Grades 6-12)";
  "7-12": "District License (Grades 7-12)";
  "9-12": "District License (Grades 9-12)";
  "6-8": "District License (Grades 6-8)";
  "7-8": "District License (Grades 7-8)";
  school: "School License";
  school_custom: "School License";
  individual: "Individual License";
  reverseLookup: (type: TLicenseType) => TGradeRange;
} = {
  "6-12": "District License (Grades 6-12)",
  "7-12": "District License (Grades 7-12)",
  "9-12": "District License (Grades 9-12)",
  "6-8": "District License (Grades 6-8)",
  "7-8": "District License (Grades 7-8)",
  school: "School License",
  school_custom: "School License",
  individual: "Individual License",
  reverseLookup: (type: TLicenseType): TGradeRange =>
    (Object.keys(rangeToFullType) as Array<keyof typeof rangeToFullType>).find(
      (key) => (rangeToFullType[key] as TLicenseType) === type
    ) as TGradeRange,
};

export const getGradeScope = (type: TLicenseType): string => {
  const match = type.match(/Grades \d+-\d+/);
  if (match) {
    return match[0];
  } else {
    return "";
  }
};

export const rosteringFullNames: {
  [key in TIntegration]:
    | "ClassLink/OneRoster"
    | "Clever"
    | "Schoology"
    | "Canvas"
    | "None";
} = {
  none: "None",
  clever: "Clever",
  "classlink/oneroster": "ClassLink/OneRoster",
  classlink: "ClassLink/OneRoster",
  schoology: "Schoology",
  canvas: "Canvas",
};

export const lmsFullNames: {
  [key in "none" | "schoology" | "canvas"]: "Schoology" | "Canvas" | "None";
} = {
  none: "None",
  schoology: "Schoology",
  canvas: "Canvas",
};

export enum RenewalActionType {
  VIEW_PAGE = "view page",
  DOWNLOAD_QUOTE = "download quote",
  DM_QUOTE_MACHINE = "dm quote machine",
  USER_QUOTE_MACHINE = "user quote machine",
  DM_QUOTE_SCRIPT = "deltamath quote (auto)",
  USER_QUOTE_SCRIPT = "user quote (auto)",
}

export type MailListEntry = {
  /** The link to the renewal page. */
  link: string;
  /** The email to whom the link should be sent. */
  email: string;
  /** The name of the customere (ie. the school name). */
  siteName?: string;
  /** The state where the site is located in */
  state?: string;
  /** The first name of the contact receiving the email (if known). */
  firstName?: string;
};

export type NotQualifiedRenewalReason =
  | "snn"
  | "individual-snn"
  | "8-12"
  | "ncesFlag"
  | "sendCustomerEmailFlag";

export type InvalidMailListEntry = MailListEntry & {
  /** The reasons why the renewal does not qualify for a renewal email. */
  reasons: NotQualifiedRenewalReason[];
  /** The optional array of reasons why the sendCustomerEmailFlag is present */
  sendCustomerEmailFlagReasons?: string[];
};

export type LicenseOption = {
  /**
   * The unique identifier for the license option.
   */
  pathId: string;

  /**
   * The type of license.
   */
  type: TLicenseType;
  /**
   * The tier of the license.
   */
  tier: "Integral" | "Plus";
  /**
   * The price of the license.
   */
  price: number;
  /**
   * The nces id of the school or district.
   */
  nces: string;
  /**
   * The name of the school or district.
   */
  name: string;
  /**
   * The address of the school or district.
   */
  address: string;
  /**
   * Optional flag to indicate if a customer email would be sent.
   */
  sendCustomerEmail?: boolean;
  /**
   * Optional reason why a customer email would not be sent.
   */
  noCustomerEmailReason?: string;

  /**
   * Optional flag to indicate if the entry in the upgrade path is the same as their current license.
   */
  isCurrentLicense?: boolean;
};

export type PlusLicenseOption = Array<[LicenseOption, LicenseOption]>;

export type IntegralLicenseOption = Array<LicenseOption>;

export type LicenseOptions = PlusLicenseOption | IntegralLicenseOption;

export interface IRenewal {
  _id: string;
  schoolYear: string;
  licenseName: string;
  nces: string;
  type: TLicenseRange;
  weight: number;
  coveredSchools?: string[];
  contacts: string[];
  renewalWeight: number;
  hasIntegral?: boolean;
  integrations?: TIntegration;
  individualPlus?: number;
  individualIntegral?: number;
  quotes?: string[];
  renewalQuoteMetadata?: Array<{
    email: string;
    when: Date;
    action: RenewalActionType;
    nces: string;
    weight: number;
    hasIntegral?: boolean;
    individualPlus?: number;
    individualIntegral?: number;
    quoteNumber?: number;
  }>;
  renewalCleared?: boolean;
  renewalClearedTime?: Date;
  renewalPlusDowngrade?: boolean;
  renewalPlusDowngradeCleared?: boolean;
  renewalGradeDowngrade?: boolean;
  renewalGradeDowngradeCleared?: boolean;
  nonRenewalConfirmed?: boolean;
  renewalNotes?: string;
  newCustomerFlag?: boolean;
  links?: Array<MailListEntry | InvalidMailListEntry>;
  is_trial?: boolean;
  upgradePath?: LicenseOptions;
  customerName?: string;
  createdAt: Date;
  updatedAt: Date;
  manuallyClearedBy?: { email: string; time: number };
  displayName?: string; //for front-end only - used in table view
  autoCleared?: boolean; //for front-end only - used to disable unchecking of cleared renewals
}

/**
 * The shape of the renewal data for display in the manager table, subset of a
 * full renewal with some additional calculated properties
 */
export type RenewalTableItem = Pick<
  IRenewal,
  | "_id"
  | "type"
  | "contacts"
  | "renewalCleared"
  | "nces"
  | "integrations"
  | "renewalQuoteMetadata"
  | "renewalPlusDowngrade"
  | "renewalGradeDowngrade"
  | "nonRenewalConfirmed"
  | "renewalNotes"
  | "individualPlus"
  | "individualIntegral"
> & {
  name: string;
  renewalWeightCalculated: number;
  currentLicense: string;
};

/**
 * a utility function to take older license types and convert them to the one of now standardized types. May ultimately be irrelevant since newer quotes adhere to the new types and we may run a migration for older ones. Not to be confused w/ discriminateLicenseType, which just helps determine a general category of license type (by, for instance, grouping all district types - 6-12, 9-12, etc. - as "district").
 * @param licenseType
 */
export const coerceLicenseType = (
  licenseType: string
): InvoiceSiteLicenseType | "" => {
  if (isValidLicenseType(licenseType)) {
    return licenseType as InvoiceSiteLicenseType;
  }
  let returnType = licenseType;
  // TODO 04/14/25 - remove after debugging
  console.log("coerceLicenseType", { licenseType });
  if (returnType.toLowerCase().includes("grades 6-12")) {
    returnType = "6-12";
  } else if (returnType.toLowerCase().includes("grades 9-12")) {
    returnType = "9-12";
  } else if (returnType.toLowerCase().includes("grades 6-8")) {
    returnType = "6-8";
  } else if (returnType.toLowerCase().includes("grades 8-12")) {
    returnType = "8-12";
  } else if (returnType.toLowerCase().includes("grades 7-12")) {
    returnType = "7-12";
  } else if (returnType.toLowerCase().includes("grades 7-8")) {
    returnType = "7-8";
  } else if (returnType.toLowerCase().includes("school")) {
    returnType = "school";
  } else returnType = "";
  return returnType as InvoiceSiteLicenseType | "";
};

export const isValidLicenseType = (licenseType: string): boolean => {
  return [
    "6-12",
    "9-12",
    "6-8",
    "8-12",
    "7-12",
    "7-8",
    "school",
    "individual",
    "school_custom",
  ].includes(licenseType);
};

export const getIndividualQuoteMachineLink = (params: {
  email: string;
  nces: string;
  license?: "Plus" | "Integral" | "";
  licenseCount?: string;
  name?: string;
  noCompare?: string;
}) => {
  const baseURL = `https://deltamath.com/teachers-schools/`;

  const baseParams = {
    createQuote: "true",
    quoteType: "individual",
    lms: "none",
  };

  if (params.license === "") {
    delete params.license;
  }

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([key, value]) => value != null)
  );

  const searchParams = new URLSearchParams({
    ...baseParams,
    ...filteredParams,
  });

  return `${baseURL}?${searchParams.toString()}`;
};

export function isSchoolNCES_ID(ncesID: string): boolean {
  return /^[0-9]{12}$/.test(String(ncesID));
}

export function isDistrictNCES_ID(ncesID: string): boolean {
  return /^[0-9]{7}$/.test(String(ncesID));
}

export function getGradesByLicenseType(licenseType: TGradeRange): string[] {
  const GRADES_BY_LICENSE_TYPE = {
    "8-12": ["8", "9", "10", "11", "12"],
    "9-12": ["9", "10", "11", "12"],
    "6-8": ["6", "7", "8"],
    "6-12": ["6", "7", "8", "9", "10", "11", "12"],
    "7-12": ["7", "8", "9", "10", "11", "12"],
    "7-8": ["7", "8"],
    school: [
      "K",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
      "11",
      "12",
    ],
  };
  return GRADES_BY_LICENSE_TYPE[licenseType] || [];
}

export const PossibleGrades = [
  "PK",
  "K",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
] as const;

type PossibleGrade = typeof PossibleGrades[number];

export function getGradeRange(
  lowGrade: string,
  highGrade: string
): PossibleGrade[] {
  const lowIndex = PossibleGrades.indexOf(lowGrade as PossibleGrade);
  const highIndex = PossibleGrades.indexOf(highGrade as PossibleGrade);

  if (lowIndex === -1 || highIndex === -1) {
    return [];
  }

  return PossibleGrades.slice(lowIndex, highIndex + 1);
}

//This function will determine whether a given school in would be covered by a given district plan. It uses similar logic to what we are trying to implement in the backend, for instance in the doesLicenseCoverSchool static in the district model. That method is specifically designed to take a current district license and determine whether a given school is covered by it. This function is more general and can be used to determine whether a school is covered by any license type. It is used to help us detect cases where the client is sending a district NCES to get a license tree but the DM license will actually be on a school since the license was converted on activation (b/c there was only one eligible school for the quoted district product)
export function schoolIsCoveredByLicenseType(
  licenseType: TGradeRange,
  school: ISchoolPopData
): boolean {
  const { lowGrade, highGrade } = school;

  if (typeof lowGrade === "undefined" || typeof highGrade === "undefined") {
    return true;
  }

  const coveredGrades = getGradesByLicenseType(licenseType);

  return (
    coveredGrades.includes(lowGrade) ||
    coveredGrades.includes(highGrade) ||
    intersection(coveredGrades, getGradeRange(lowGrade, highGrade)).length > 0
  );
}

export function getIntegrationsString(quote: IInvoice) {
  const res: string[] = [];
  if (quote.lms) {
    res.push(quote.lms.toLowerCase());
  }
  if (quote.rosteringSystem && quote.rosteringSystem === "Clever") {
    res.push("clever");
  } else if (
    quote.rosteringSystem &&
    quote.rosteringSystem === "ClassLink/OneRoster"
  ) {
    res.push("classlink");
  }
  return res.length === 0 ? undefined : res.join(", ");
}

export function predictExpiration(
  schoolYear: string,
  validationArray?: Array<Date>
): number {
  let val = 0;
  if (!schoolYear) {
    return 0;
  }
  if (
    schoolYear === CURRENT_PRORATED_LICENSE ||
    schoolYear === CURRENT_SCHOOL_YEAR ||
    schoolYear === NEXT_SCHOOL_YEAR
  ) {
    val = EXPIRATIONS[schoolYear];
  } else {
    const year = parseInt(schoolYear.split("-")[1], 10);
    if (!isNaN(year)) {
      val = Math.floor(
        new Date(`${2000 + year}-10-01T23:59:00-04:00`).getTime() / 1000
      );
    }
  }

  //On the invoice page there is a drop down menu. For safety, don't want to set payload w/ something that isn't in the dropdown. 0 here corresponds to the "--" option in the dropdown
  if (validationArray?.length) {
    const validationVals = validationArray.map((date) =>
      Math.floor(date.getTime() / 1000)
    );
    if (val && !validationVals.includes(val)) {
      val = 0;
    }
  }
  return val;
}

export interface IActivationSummary {
  numStudents: number;
  numTeachers: number;
  adminEmail?: string;
  type?: string;
  tier: string;
  rostering: string;
  lms: string;
}

export interface IActivationPayload {
  hold_until?: number;
  hiddenFromView?: boolean;
  quoteNumber?: string;
  schoolYear?: string;
  id?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  type?: string;
  name?: string;
  status?: string | null;
  poNumber?: string;
  poUploadedTime?: number;
  contactName?: string;
  contactEmail?: string;
  adminFirst?: string;
  adminLast?: string;
  adminEmail?: string;
  notes?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  priceInfo: {
    price?: number;
    numberOfStudents: number;
    numberOfTeachers: number;
    discount?: number;
    priceWithDiscount?: number;
    minPrice?: number;
    sendCustomerEmail?: boolean;
    // licenseType?: string;
  };
  amount?: number;
  licenseTier: string;
  lms: string;
  rosteringSystem: string;
  integrationStatus?: string;
  cleverDistrictId?: string;
  classlinkTenantId?: string;
  site_timezone?: string;
  expiration?: number;
  time?: number;
  quoteId?: string;
  customerId?: string;
  rosteringData?: {
    [key: string]: any[];
  };
  snnLicenseType?: string | null;
}

export const defaultResetPayload: IActivationPayload = {
  id: "", //ncesID -- could be renamed in the Invoice model.
  type: "",
  name: "",
  status: null,
  poNumber: "",
  invoiceId: "",
  invoiceNumber: "",
  contactName: "",
  contactEmail: "",
  adminFirst: "",
  adminLast: "",
  adminEmail: "",
  notes: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  priceInfo: {
    price: 0,
    numberOfStudents: 0,
    numberOfTeachers: 0,
    discount: 0,
    priceWithDiscount: 0,
    minPrice: 0,
    sendCustomerEmail: true,
  },
  hold_until: undefined,
  amount: 1,
  licenseTier: "",
  lms: "",
  rosteringSystem: "",
  integrationStatus: "",
  site_timezone: "",
  cleverDistrictId: "",
  classlinkTenantId: "",
  rosteringData: {},
  snnLicenseType: null,
  expiration: 0, //not using an actual date as a default to enforce an active choice prior to activation
};

export interface IInvoice {
  _id: string;
  type: string;
  id?: string;
  source?: "QuoteMachine" | "Renewal";
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  contactName?: string;
  contactEmail: string;
  adminFirst?: string;
  adminLast?: string;
  adminEmail?: string;
  customerId?: string;
  quoteId?: string;
  quoteNumber: string;
  invoiceId?: string;
  invoiceNumber?: string;
  poNumber: string;
  poUploadedTime?: number;
  activatedTime?: number;
  hold_until?: number | null;
  notes: string;
  time: number;
  priceInfo: {
    discount?: number;
    licenseType?: string;
    minPrice?: number;
    nces_notes?: string;
    noCustomerEmailReason?: string;
    numberOfStudents?: number;
    numberOfTeachers?: number;
    price: number;
    priceWithDiscount?: number;
    sendCustomerEmail?: boolean;
    flaggedSchool?: {
      highGrade: number;
      lowGrade: number;
      name: string;
      nces_id: string;
      students: number;
    };
  };
  amount: number;
  licenseTier?: string;
  individualSiteType?: string;
  rosteringSystem?:
    | "ClassLink/OneRoster"
    | "Clever"
    | "Schoology"
    | "Canvas"
    | "";
  integrationStatus?: "requested" | "verified";
  cleverDistrictId?: string;
  classlinkTenantId?: string;
  rosteringData?: {
    [key: string]: Array<string | { id: string; name: string }>;
  };
  lms?: "Schoology" | "Canvas" | "";
  schoolYear?: string;
  schools?: ISchoolPopData;
  site_timezone?: string;
  printed_date?: number;
  emailed_date?: number;
  mailed_date?: number;
  sent_ack?: number;
  expiration?: number;
  status?:
    | "PO Received"
    | "Invoice Requested"
    | "Other - Random Check Received"
    | "Incorrect Info - Requested Updated PO"
    | "Invoice Created"
    | "Payment Confirmed"
    | "Activated"
    | ""
    | "PO Received (Needs Updating)"
    | "PO Received (Good)"
    | "CC/Check Payment Requested"
    | "CC/Check Payment Received";
  order_received?: string;
  inferredFromQBO?: boolean;
  memo?: string;
  lastUpdated?: number;
  hiddenFromView?: boolean;
  snnLicenseType?: string;
  sendToOrders?: boolean;
  cancelled?: boolean;
  poInfo?: {
    id: string;
    filename: string;
    /** Path to the file on QBO - only available on attachments made after March '25 */
    fileUrl?: string;
  };
}

export interface RosteringDataForInvoice {
  integrationData: Array<{
    id: string;
    name: string;
    nces_id?: string;
    likelyMatch?: string;
  }>;
  diggerSchools: Array<{ nces_id: string; name: string }>;
}

interface IFoundLicenseForInvoice {
  //generally we would only search for licenses that are active, but if we are matching explicitly on a quoteNumber, we may want to have info on an expired license
  status: "active" | "expired";
  type: "school" | TGradeRange | "individual" | "snn" | "district";
  matchedOn: string;
  expiration: Date;
  name: string;
  quoteNumber?: string;
  tier?: "integral" | "plus";
  invoice?: number;
  //flag for a school that has its own license and is covered by a district license
  isDoubleLicense?: boolean;
  //flag for case where the client may be viewing a district quote, but the activated license was for a school
  convertedToSchool?: boolean;
  is_trial?: boolean;
}

interface INoLicenseFound {
  status: "none";
  type: IFoundLicenseForInvoice["type"];
  matchedOn?: string;
}

export interface IFoundIndividualLicenseForInvoice
  extends IFoundLicenseForInvoice {
  totalSubscriptions: number;
  subscriptionsRemaining: number;
  accessCode: string;
  contact: string;
}

export interface IFoundSiteLicenseForInvoice extends IFoundLicenseForInvoice {
  siteID: string;
  students: number;
  teachers: number;
  primaryAdmin: { email: string; first: string; last: string } | "Not Found";
  cleverDistrictID?: string | Array<string>;
  classlinkTenantID?: string | number;
  integrations?: string;
  is_trial?: boolean;
}

export type IIndividualLicenseForInvoice =
  | IFoundIndividualLicenseForInvoice
  | INoLicenseFound;

export type ISiteLicenseForInvoice =
  | IFoundSiteLicenseForInvoice
  | INoLicenseFound;

export interface ILicenseSearchResult {
  school?: ISiteLicenseForInvoice;
  district?: ISiteLicenseForInvoice;
  individual?: {
    plus?: IFoundIndividualLicenseForInvoice[];
    integral?: IFoundIndividualLicenseForInvoice[];
    none?: INoLicenseFound;
  };
}

export interface IChangeAdminStatusPayload {
  newAdminID?: string;
  currentAdminID?: string;
  newAdminEmail?: string;
  newAdminFirst?: string;
  newAdminLast?: string;
  downgradeOnly?: boolean;
  currentAdminEmail?: string;
  newAdminAccountType?:
    | "school"
    | "district"
    | "super_school"
    | "super_district";
}

export interface INewAdminPayload {
  siteType: "school" | "district";
  siteId: string;
  userData: {
    firstName: string;
    lastName: string;
    email: string;
    account_type?: "school" | "district" | "super_school" | "super_district";
  };
  integrations: string;
}
